import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { AddressJSON } from "_shared/addresses/address-schema";
import { api } from "_shared/_stores/api";
import Guid from "_shared/guids/guid";

/**
 * What became of one row, in the only two shapes the grid cares about.
 *
 * Both endpoints name their outcomes the same way, and the officer one has an outcome the member
 * one does not: a firm already on Attorney Hub has no letter sent and no invitation created. Both
 * collapse to invited-or-rejected plus a note, because what the grid does next is the same: clear
 * the row, or keep it and say why.
 */
export type BulkInvitationOutcomeKind = "invited" | "rejected";

export type BulkInvitationResultRow = {
  readonly index: number;
  readonly email: string;
  readonly outcome: BulkInvitationOutcomeKind;
  /** Something true of a row that succeeded anyway, shown beside it in the summary. */
  readonly note?: string;
  readonly error?: string;
};

type BulkInvitationResultJSON = {
  index?: number;
  email?: string;
  /**
   * The outcome's name. Both endpoints spell it that way - `InviteEntityOfficerRowResponse.Outcome`
   * is a string the API writes with `.ToString()`, and `BulkEntityMemberInvitationOutcome` is
   * declared `[JsonConverter(typeof(StringEnumConverter))]` so it crosses the wire as a name too.
   */
  outcome?: string;
  invitationId?: string | null;
  rolloutId?: string | null;
  error?: string | null;
};

export type TeamMemberRosterRow = {
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly title: string;
  readonly requestedMarketplaceVisibility: boolean;
  readonly accessTemplateId?: string;
};

export type TeamLeaderRosterRow = {
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly title: string;
  readonly entityName: string;
  readonly entityLocation: AddressJSON;
  readonly message?: string;
};

type InviteTeamMembersArgs = {
  readonly entityId: Guid;
  readonly vendorRolloutId?: Guid;
  readonly rows: readonly TeamMemberRosterRow[];
};

type InviteTeamLeadersArgs = {
  readonly sponsoringClientLegalEntityId: Guid;
  readonly sponsoringClientRepresentativeUserId: Guid;
  readonly onboardingTemplateId?: string;
  readonly rows: readonly TeamLeaderRosterRow[];
};

const jsonHeaders = { "Content-Type": "application/json" };

/**
 * The outcome names that mean a row was acted on. `Invited` is both endpoints' success;
 * `OpenedForEstablishedFirm` is the officer endpoint's, and the member endpoint never sends it.
 *
 * Everything else is a rejection, which is the safe way round and needs no branch of its own. A
 * name read as a success would clear a row for somebody who may not have been invited, and they
 * are then simply gone; kept as a rejection the row stays, and a person who really was invited
 * meets "already has an open invitation" on the retry, which is recoverable and says so.
 */
const INVITED_OUTCOMES: ReadonlySet<string> = new Set(["Invited", "OpenedForEstablishedFirm"]);

function toOutcomeKind(outcome: string | undefined): BulkInvitationOutcomeKind {
  return outcome !== undefined && INVITED_OUTCOMES.has(outcome) ? "invited" : "rejected";
}

function toResultRow(
  json: BulkInvitationResultJSON,
  fallbackIndex: number,
  noteFor?: (json: BulkInvitationResultJSON) => string | undefined,
): BulkInvitationResultRow {
  const index = typeof json.index === "number" ? json.index : fallbackIndex;
  const email = json.email ?? "";
  const outcome = toOutcomeKind(json.outcome);

  if (outcome === "rejected") {
    return { index, email, outcome, error: json.error ?? "This row could not be invited." };
  }

  return { index, email, outcome, note: noteFor?.(json) };
}

/**
 * A firm that was already on Attorney Hub. Its roll-out opened at the client-proposal stage and no
 * letter went out about an account that already exists, which is worth telling whoever entered the
 * list: they will not see an invitation for that firm anywhere.
 */
function establishedFirmNote(json: BulkInvitationResultJSON): string | undefined {
  return json.outcome === "OpenedForEstablishedFirm"
    ? "Already on Attorney Hub - the roll-out opened without sending an invitation."
    : undefined;
}

export const bulkInvitationApi = api.injectEndpoints({
  endpoints: (build) => ({
    inviteTeamMembers: build.mutation<readonly BulkInvitationResultRow[], InviteTeamMembersArgs>({
      async queryFn({ entityId, vendorRolloutId, rows }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: `legal-entities/${entityId.value}/user-entity-member-invitations/bulk`,
          method: "POST",
          body: JSON.stringify({ vendorRolloutId: vendorRolloutId?.value, rows }),
          headers: jsonHeaders,
        });

        if (response.error) {
          return { error: toQueryError(new BulkInvitationAPIServiceError("inviteTeamMembers", response.error)) };
        }

        // 200 and 207 both land here: a 207 is a success as far as fetch is concerned, and the
        // per-row outcomes are what says whether anything needs attention.
        const results = (response.data as BulkInvitationResultJSON[]).map((json, position) =>
          toResultRow(json, position),
        );
        return { data: results };
      },
      invalidatesTags: (_result, _error, { entityId }) => [
        { type: "UserEntityMemberInvitation" as const, id: `LegalEntity-${entityId.value}` },
      ],
    }),

    inviteTeamLeaders: build.mutation<readonly BulkInvitationResultRow[], InviteTeamLeadersArgs>({
      async queryFn(
        { sponsoringClientLegalEntityId, sponsoringClientRepresentativeUserId, onboardingTemplateId, rows },
        _queryApi,
        _extraOptions,
        baseQuery,
      ) {
        const response = await baseQuery({
          url: "super-user/invite-entity-officers",
          method: "POST",
          // Flat, and named exactly as `InviteEntityOfficersCommand` declares them. The domain
          // command nests the pair under a `SponsoringClient`, but the wire contract does not:
          // an unrecognised property is dropped silently, so a nested sponsor arrives as two
          // empty Guids and the submission is rejected whole for having no sponsoring client.
          body: JSON.stringify({
            sponsoringClientLegalEntityId: sponsoringClientLegalEntityId.value,
            sponsoringClientRepresentativeUserId: sponsoringClientRepresentativeUserId.value,
            onboardingTemplateId,
            rows,
          }),
          headers: jsonHeaders,
        });

        if (response.error) {
          return { error: toQueryError(new BulkInvitationAPIServiceError("inviteTeamLeaders", response.error)) };
        }

        const results = (response.data as BulkInvitationResultJSON[]).map((json, position) =>
          toResultRow(json, position, establishedFirmNote),
        );
        return { data: results };
      },
      invalidatesTags: ["EntityOfficerInvitation", "VendorRolloutBoard"],
    }),
  }),
});

export class BulkInvitationAPIServiceError extends Error {
  public readonly method: string;
  public readonly error: unknown;

  public constructor(method: string, error: unknown) {
    super(`BulkInvitationAPIService: ${error instanceof Error ? error.message : String(error)}`);
    this.name = "BulkInvitationAPIServiceError";
    this.method = method;
    this.error = error;
  }
}

function toQueryError(error: Error): FetchBaseQueryError {
  return error as unknown as FetchBaseQueryError;
}

export const { useInviteTeamMembersMutation, useInviteTeamLeadersMutation } = bulkInvitationApi;
